import Login from './components/public/Login';
import Dashboard from './components/Dashboard';

import ApiClientProvider from './api/apiClientProvider';
import { createApiClient } from './api/rootClient';

import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from './utils/env';
import axios from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Organisation } from './components/types/dashboard';
import { DASHBOARD_ACCESS } from './components/types/constant';
import { localStorageToJSON } from './utils/common';
import CreatePassword from './components/public/CreatePassword';
import ForgotPassword from './components/public/ForgotPassword';
import PageNotFound from './components/public/PageNotFound';

const apiClient = createApiClient(getApiBaseUrl());

axios.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token && config.headers && /localhost|pixels\.ai/.test(config?.url || '')) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.response?.status === 401 && window.localStorage) {
      localStorage.setItem('token', '');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error.response?.data?.message ?? error);
  }
);

type OrganisationCxt = {
  organisations: Organisation[],
  selectedOrganisationObject: Organisation | undefined,
  setOrganisations: React.Dispatch<React.SetStateAction<Organisation[]>>
  selectedOrganisation: string | null,
  setSelectedOrganisation: React.Dispatch<React.SetStateAction<string | null>>,
  setCurrentAccess: React.Dispatch<React.SetStateAction<DASHBOARD_ACCESS>>,
  currentAccess: DASHBOARD_ACCESS,
  isAdmin: boolean
}

export const OrganisationContext = createContext<OrganisationCxt>({} as OrganisationCxt);

function App() {
  const [selectedOrganisation, setSelectedOrganisation] = useState<string | null>(null);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [currentAccess, setCurrentAccess] = useState<DASHBOARD_ACCESS>(DASHBOARD_ACCESS.UNSET);

  return (
    <ApiClientProvider value={apiClient}>
      <OrganisationContext.Provider value={{
        selectedOrganisation,
        organisations,
        selectedOrganisationObject: organisations.find(({ domain }) => domain === selectedOrganisation),
        setOrganisations,
        setSelectedOrganisation,
        currentAccess,
        setCurrentAccess,
        isAdmin: localStorage.getItem('is_admin') === 'true'
      }}>
        <BrowserRouter>
          <Routes>
            {/* AUTH ROUTES */}
            <Route path="/create-password/" element={<CreatePassword title={'Create Password'}/>}/>
            <Route path="/forgot-password/" element={<ForgotPassword/>}/>
            <Route path="/reset-password/" element={<CreatePassword title={'Reset Password'}/>}/>
            <Route path="/login/" element={<Login/>}/>
            <Route path="/login" element={<Navigate to="/login/" />}/>
            {/* DASHBOARD ROUTES */}
            <Route
              path="/"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="report"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/page-view"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="pv-report"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/video-swap"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="video-swap"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/video-swap/no-matches"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="video-swap-no-matches"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/video-swap/swapped"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="video-swap-swapped"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/video-swap/improve"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="video-swap-improve"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/promoted-content/1"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="promoted-content-1"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/promoted-content/2"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="promoted-content-2"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/promoted-content/3"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="promoted-content-3"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/calculator"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="calculator"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/headline-search"
              element={
                <RequireAuth>
                  <CheckRouteAccess access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER]}>
                    <Dashboard section="headline-search"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/video-library"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_RIGHTSHOLDER]}>
                    <Dashboard section="video-library"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/pixels-studio/:tab?"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.VIDEO_RIGHTSHOLDER]}>
                    <Dashboard section="pixels-studio"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/trends-and-videos"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_RIGHTSHOLDER]}>
                    <Dashboard section="trends-and-videos"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/playlist"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_PUBLISHER, DASHBOARD_ACCESS.CAMPAIGN_ORGANISATION]}>
                    <Dashboard section="playlist"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/playlist/properties?/:playlist"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_PUBLISHER]}>
                    <Dashboard section="playlist-video"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/playlist/creative?/:playlist"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_PUBLISHER]}>
                    <Dashboard section="playlist-creative"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
             <Route
              path="/playlist/creative?/:playlist/:creativeId/tags"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_PUBLISHER]}>
                    <Dashboard section="playlist-creative-tags"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/playlist/creative?/:playlist/:creativeId/tag/:tagId/deals"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_PUBLISHER]}>
                    <Dashboard section="playlist-creative-tag-deals"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/playlist/popular-article/:playlist"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_PUBLISHER]}>
                    <Dashboard section="playlist-popular-article"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/playlist/campaign-delivery/:playlist"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_PUBLISHER]}>
                    <Dashboard section="playlist-campaign-delivery"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />
            <Route
              path="/gap-analysis"
              element={
                <RequireAuth>
                  <CheckRouteAccess
                    access={[DASHBOARD_ACCESS.NON_RIGHTSHOLDER, DASHBOARD_ACCESS.VIDEO_PUBLISHER]}>
                    <Dashboard section="gap-analysis"/>
                  </CheckRouteAccess>
                </RequireAuth>
              }
            />

            {/* ADMIN ROUTES */}
            <Route
              path="/admin/opus-jobs"
              element={
                <RequireAuth>
                  <Dashboard demoPage adminPage section="opus-jobs"/>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/organisations"
              element={
                <RequireAuth>
                  <Dashboard adminPage section="organisations"/>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequireAuth>
                  <Dashboard adminPage section="users"/>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/users/add"
              element={
                <RequireAuth>
                  <Dashboard adminPage section="user-add"/>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/users/edit/:id"
              element={
                <RequireAuth>
                  <Dashboard adminPage section="user-edit"/>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/calculator"
              element={
                <RequireAuth>
                  <Dashboard demoPage section="calculator"/>
                </RequireAuth>
              }
            />
            {/* 404 - must be last */}
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </BrowserRouter>
      </OrganisationContext.Provider>
    </ApiClientProvider>
  );
}

function RequireAuth({ children }) {
  const token = window.localStorage && window.localStorage.getItem('token');
  return token ? children : <Navigate to="/login"/>;
}

function CheckRouteAccess({ children, access }: {
  access: DASHBOARD_ACCESS[],
  children: JSX.Element
}) {
  const navigate = useNavigate();
  const {
    setSelectedOrganisation,
    setOrganisations,
    organisations,
    selectedOrganisation,
    setCurrentAccess,
    currentAccess
  } = useContext(OrganisationContext);

  useEffect(() => {
    const organisations = localStorageToJSON('pubAccess');
    setOrganisations(organisations);
    const domains = organisations.map(({ domain }) => domain);
    const selectedPublication = localStorage.getItem('selectedOrganisation');
    //ensure selected domain is actually in the list of domains...
    const org = organisations.find(({ domain }) => domain === selectedPublication);
    setSelectedOrganisation(org?.domain || domains[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedOrganisation) return;
    localStorage.setItem('selectedOrganisation', selectedOrganisation);
    const org = organisations.find(({ domain }) => domain === selectedOrganisation);

    if (org?.is_demo) {
      setCurrentAccess(DASHBOARD_ACCESS.DEMO);
    } else if (org?.is_rightsholder) {
      setCurrentAccess(DASHBOARD_ACCESS.VIDEO_RIGHTSHOLDER);
    } else if (org?.is_publisher) {
      setCurrentAccess(DASHBOARD_ACCESS.VIDEO_PUBLISHER);
    } else if (org?.is_campaign) {
      setCurrentAccess(DASHBOARD_ACCESS.CAMPAIGN_ORGANISATION);
    } else {
      setCurrentAccess(DASHBOARD_ACCESS.NON_RIGHTSHOLDER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisation]);

  useEffect(() => {
    if (access.includes(currentAccess)) return;

    switch (currentAccess) {
      case DASHBOARD_ACCESS.VIDEO_PUBLISHER:
        navigate('/playlist');
        break;
      case DASHBOARD_ACCESS.VIDEO_RIGHTSHOLDER:
        navigate('/video-library');
        break;
      // case DASHBOARD_ACCESS.UNSET
      //   // noop
      //   break;
      default:
        navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccess]);


  return children;
}

export default App;
